import React, { memo, useEffect, useLayoutEffect, useRef } from "react";
import CardFrameSurface from "../../../components/CardFrameSurface";
import CardMotionFrame, { CardMotionStyles } from "./CardMotionFrame";

const StaticWallCards = ({
  cards,
  header,
  isLoading = false,
  emptyMessage = "No public MCAAD wall cards are available yet.",
  onCardClick,
  hasMore = false,
  isLoadingMore = false,
  loadMoreError = false,
  onLoadMore,
  onRetryLoadMore,
  arrivalCardId = "",
  highlightedCardId = "",
  onArrivalTargetRendered,
  wallId = "",
  scrollResetKey = "",
}) => {
  const scrollContainerRef = useRef(null);
  const loadMoreSentinelRef = useRef(null);
  const loadMoreTriggeredRef = useRef(false);
  const scrollCheckTimeoutRef = useRef(null);
  const arrivedCardHandledRef = useRef("");
  const previousWallIdRef = useRef(wallId);
  const previousScrollResetKeyRef = useRef(scrollResetKey);
  const wallSwitchPendingRef = useRef(false);

  useLayoutEffect(() => {
    const wallChanged = previousWallIdRef.current !== wallId;
    const entryChanged = previousScrollResetKeyRef.current !== scrollResetKey;
    if (!wallChanged && !entryChanged) {
      return;
    }

    previousWallIdRef.current = wallId;
    previousScrollResetKeyRef.current = scrollResetKey;
    arrivedCardHandledRef.current = "";
    wallSwitchPendingRef.current = wallChanged;
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [scrollResetKey, wallId]);

  useLayoutEffect(() => {
    if (wallSwitchPendingRef.current) {
      wallSwitchPendingRef.current = false;
      return;
    }

    if (!arrivalCardId || isLoading) {
      return;
    }

    const scrollContainer = scrollContainerRef.current;
    const targetCard = Array.from(
      scrollContainer?.querySelectorAll("[data-wall-card-id]") || []
    ).find((card) => card.dataset.wallCardId === arrivalCardId);

    if (!scrollContainer || !targetCard) {
      return;
    }

    if (arrivedCardHandledRef.current !== arrivalCardId) {
      const containerTop = scrollContainer.getBoundingClientRect().top || 0;
      const cardTop = targetCard.getBoundingClientRect().top || 0;
      scrollContainer.scrollTop += cardTop - containerTop;
      arrivedCardHandledRef.current = arrivalCardId;
      onArrivalTargetRendered?.(arrivalCardId);
    }
  }, [arrivalCardId, cards, isLoading, onArrivalTargetRendered]);

  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    const scrollContainer = scrollContainerRef.current;

    if (
      !sentinel ||
      !scrollContainer ||
      !hasMore ||
      typeof onLoadMore !== "function"
    ) {
      return undefined;
    }

    loadMoreTriggeredRef.current = false;

    const triggerLoadMore = () => {
      if (loadMoreTriggeredRef.current) {
        return;
      }

      loadMoreTriggeredRef.current = true;
      onLoadMore();
    };

    const checkProximity = () => {
      const distanceFromBottom =
        scrollContainer.scrollHeight -
        scrollContainer.scrollTop -
        scrollContainer.clientHeight;

      if (distanceFromBottom <= 240) {
        triggerLoadMore();
      }
    };

    if (typeof IntersectionObserver === "undefined") {
      const handleScroll = () => {
        if (scrollCheckTimeoutRef.current !== null) {
          return;
        }

        scrollCheckTimeoutRef.current = setTimeout(() => {
          scrollCheckTimeoutRef.current = null;
          checkProximity();
        }, 50);
      };

      scrollContainer.addEventListener("scroll", handleScroll, {
        passive: true,
      });
      checkProximity();

      return () => {
        scrollContainer.removeEventListener("scroll", handleScroll);

        if (scrollCheckTimeoutRef.current !== null) {
          clearTimeout(scrollCheckTimeoutRef.current);
          scrollCheckTimeoutRef.current = null;
        }
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          triggerLoadMore();
        }
      },
      { root: scrollContainer, rootMargin: "0px 0px 240px 0px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [cards.length, hasMore, onLoadMore]);

  return (
  <>
    <CardMotionStyles />

    <div
      ref={scrollContainerRef}
      data-testid="wall-scroll-container"
      className={`absolute inset-0 z-20 w-full overflow-x-hidden ${
        isLoading
          ? "flex items-center justify-center overflow-y-hidden px-3 py-0 sm:px-4"
          : "overflow-y-auto px-3 pb-32 pt-3.5 [-webkit-overflow-scrolling:touch] sm:px-4 sm:pt-[18px]"
      }`}
    >
      <div
        className={`mx-auto w-full max-w-[1080px] ${
          isLoading
            ? "flex min-h-[100dvh] items-center justify-center px-2"
            : `px-2 pt-1 sm:px-4 sm:pt-4 ${hasMore ? "pb-2 sm:pb-4" : "pb-6 sm:pb-8"}`
        }`}
      >
        {header ? (
          <div className="mx-auto mb-1 w-[min(100%,760px)] text-center sm:mb-2">
            {header}
          </div>
        ) : null}

        {isLoading ? (
          <div
            className="flex min-h-[180px] w-full max-w-lg -translate-y-[16.6667dvh] flex-col items-center justify-center gap-3 rounded-xl border border-[#f4d94e]/12 bg-white/[0.055] p-6 text-center text-white shadow-[0_18px_46px_rgba(0,0,0,0.24)] backdrop-blur-xl"
            role="status"
            aria-live="polite"
          >
            <span
              className="h-7 w-7 rounded-full border-2 border-[#f4d94e]/25 border-t-[#f4d94e] motion-safe:animate-spin motion-reduce:opacity-70"
              aria-hidden="true"
            />
            <p className="font-serif text-2xl font-medium leading-none tracking-[-0.035em] text-white sm:text-3xl">
              Entering this wall...
            </p>
          </div>
        ) : cards.length ? (
          <div className="grid grid-cols-2 justify-items-center gap-x-1 gap-y-4 sm:grid-cols-3 sm:gap-x-2.5 sm:gap-y-5 md:grid-cols-4">
            {cards.map((card, index) => {
              const translations = Array.isArray(card.translations) && card.translations.length
                ? card.translations.slice(0, 2)
                : card.translation
                  ? [{ text: card.translation }]
                  : [];
              const hasMultipleTranslations = translations.length === 2;

              return (
                <button
                  key={`${card.id || card.question}-${index}`}
                  data-testid={`wall-card-${card.id || index}`}
                  data-wall-card-id={card.id || ""}
                  data-arrival-highlighted={String(
                    Boolean(highlightedCardId && card.id === highlightedCardId)
                  )}
                  type="button"
                  onClick={() => onCardClick(index)}
                  className={`group flex h-[242px] w-full max-w-[176px] items-center justify-center overflow-visible rounded-2xl p-1 text-center transition duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f4d94e]/55 motion-reduce:transition-none sm:h-[272px] sm:max-w-[204px] sm:p-2 md:h-[286px] md:max-w-[212px] md:p-2 lg:h-[310px] lg:max-w-[228px] lg:p-2.5 ${
                    highlightedCardId && card.id === highlightedCardId
                      ? "scale-[1.015] bg-[#f4d94e]/10 shadow-[0_0_34px_rgba(244,217,78,0.42)] ring-1 ring-[#f4d94e]/45"
                      : "hover:scale-[1.02]"
                  }`}
                >
                  <CardMotionFrame
                    index={index}
                    className="aspect-[160/247] w-[136px] drop-shadow-[0_0_28px_rgba(244,217,78,0.32)] transition-[filter] duration-300 group-hover:drop-shadow-[0_0_40px_rgba(244,217,78,0.46)] group-focus-visible:drop-shadow-[0_0_40px_rgba(244,217,78,0.46)] sm:w-[160px] sm:drop-shadow-[0_0_36px_rgba(244,217,78,0.36)] md:w-[168px] md:drop-shadow-[0_0_38px_rgba(244,217,78,0.36)] md:group-hover:drop-shadow-[0_0_44px_rgba(244,217,78,0.46)] md:group-focus-visible:drop-shadow-[0_0_44px_rgba(244,217,78,0.46)] lg:w-[184px] lg:drop-shadow-[0_0_42px_rgba(244,217,78,0.36)] lg:group-hover:drop-shadow-[0_0_48px_rgba(244,217,78,0.46)] lg:group-focus-visible:drop-shadow-[0_0_48px_rgba(244,217,78,0.46)]"
                  >
                    <CardFrameSurface className="h-full w-full">
                      <div
                        className="absolute inset-x-[15%] top-[16%] bottom-[22%] flex min-h-0 min-w-0 max-w-full flex-col items-center justify-start gap-2 overflow-hidden text-center md:gap-[9px] lg:gap-2.5"
                      >
                        <p
                          className={`min-h-0 min-w-0 w-full max-w-full shrink-0 font-bold leading-tight text-[#2f2a16] [overflow-wrap:anywhere] text-[9px] md:text-[12px] ${
                            hasMultipleTranslations
                              ? "line-clamp-6 md:line-clamp-4 lg:line-clamp-5"
                              : "line-clamp-7"
                          }`}
                          dir="auto"
                          style={{ fontFamily: "'Poppins', sans-serif" }}
                        >
                          {card.question}
                        </p>

                        {translations.length ? (
                          <div
                            className={`flex min-h-0 min-w-0 w-full flex-col ${
                              hasMultipleTranslations
                                ? "gap-1 md:gap-[4.5px] lg:gap-[5px]"
                                : "gap-2 md:gap-[9px] lg:gap-2.5"
                            }`}
                          >
                            {translations.map((translation, translationIndex) => (
                              <p
                                key={`${translation.languageCode || translation.language || "translation"}-${translationIndex}`}
                                className={`line-clamp-3 min-h-0 min-w-0 w-full max-w-full shrink-0 font-medium leading-snug text-[#5f5942] [overflow-wrap:anywhere] ${
                                  hasMultipleTranslations
                                    ? "text-[6px] md:text-[8px]"
                                    : "text-[7px] md:text-[8px]"
                                }`}
                                dir="auto"
                                style={{ fontFamily: "'Poppins', sans-serif" }}
                              >
                                {translation.text}
                              </p>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </CardFrameSurface>
                  </CardMotionFrame>
                </button>
              );
            })}
          </div>
        ) : (
          <p className="rounded-xl border border-dashed border-gray-200 bg-white/70 p-4 text-center font-sans text-sm font-bold text-gray-500">
            {emptyMessage}
          </p>
        )}

        {!isLoading && hasMore ? (
          <div
            ref={loadMoreSentinelRef}
            data-testid="wall-load-more-sentinel"
            className="mt-5 flex min-h-12 items-center justify-center"
            aria-live="polite"
          >
            {isLoadingMore ? (
              <p role="status" className="font-sans text-xs text-white/65">
                Loading more cards...
              </p>
            ) : loadMoreError ? (
              <div className="flex flex-col items-center gap-2 text-center">
                <p className="font-sans text-xs text-white/65">
                  We couldn&apos;t load more cards.
                </p>
                <button
                  type="button"
                  onClick={onRetryLoadMore}
                  className="rounded-full border border-[#f4d94e]/35 px-3 py-1.5 font-sans text-xs font-semibold text-[#f4d94e] transition hover:bg-[#f4d94e]/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f4d94e]/60"
                >
                  Try again
                </button>
              </div>
            ) : null}
          </div>
        ) : !isLoading && cards.length ? (
          <p className="mt-5 text-center font-sans text-xs text-white/45" role="status">
            You&apos;ve reached the end of this wall.
          </p>
        ) : null}

      </div>

    </div>
  </>
  );
};

export default memo(StaticWallCards);
